import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { CheckCheck, Stamp } from "lucide-react";
import {
  activateInstagramPage,
  getCurrentInstagramMe,
  getCurrentInstagramPages,
} from "../api/AppLinkApi";
import LayoutAction from "../actions/LayoutAction";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import ProfileSkeleton from "./components/ProfileSkeleton";
import PageItemSkeleton from "./components/PageItemSkeleton";
import ProfileDisplay from "./components/ProfileDisplay";
import PageItem from "./components/PageItem";
import ConnectedStatus from "./components/ConnectedStatus";

const InstagramSetting = ({ appInfo, onReloadApp }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [connectedUser, setConnectedUser] = useState({
    pageId: "",
    picture: "",
    name: t("Anonymous"),
    description: "",
    id: "",
    username: "",
    biography: null,
    followersCount: 0,
    followsCount: 0,
    mediaCount: 0,
    profilePictureUrl: "",
    website: "",
    hasProfilePic: true,
    isPublished: true,
    legacyInstagramUserId: null,
    shoppingProductTagEligibility: null,
  });
  const [connectedUserPages, setConnectedUserPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
    id: "",
    username: "",
    biography: "",
    followersCount: 0,
    followsCount: 0,
    mediaCount: 0,
    profilePictureUrl: "",
    website: "",
    hasProfilePic: true,
    isPublished: true,
    legacyInstagramUserId: null,
    shoppingProductTagEligibility: null,
  });

  useEffect(() => {
    if (appInfo.connected) {
      loadProfile();
      loadPages();
    } else {
      setLoading(false);
      setPagesLoading(false);
    }
  }, [appInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = () => {
    setLoading(true);
    getCurrentInstagramMe()
      .then((data) => {
        setConnectedUser({
          pageId: appInfo.data?.page_id,
          picture: data?.pictureUrl,
          name: data?.name || t("Anonymous"),
          description: data?.username,
          id: data?.id,
          username: data?.username,
          biography: data?.biography,
          followersCount: data?.followersCount,
          followsCount: data?.followsCount,
          mediaCount: data?.mediaCount,
          profilePictureUrl: data?.profilePictureUrl,
          website: data?.website,
          hasProfilePic: data?.hasProfilePic,
          isPublished: data?.isPublished,
          legacyInstagramUserId: data?.legacyInstagramUserId,
          shoppingProductTagEligibility: data?.shoppingProductTagEligibility,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadPages = () => {
    setPagesLoading(true);
    getCurrentInstagramPages()
      .then((data) => {
        const pages = (data || []).map((item) => ({
          id: item.id,
          instagramBusinessAccountId: item.instagram_business_account?.id,
          picture: "",
          name: item.name,
          description: item.category || "",
        }));
        setConnectedUserPages(pages);
      })
      .finally(() => {
        setPagesLoading(false);
      });
  };

  const handleMakeDefault = (page) => {
    setShowConfirm({
      show: true,
      title: t("Default Page"),
      description: t("Are you sure you want to make default this page?"),
      type: "SET_DEFAULT_PAGE",
      data: page,
    });
  };

  const handleSubmit = () => {
    if (showConfirm.type === "SET_DEFAULT_PAGE") {
      activateInstagramPage(showConfirm.data.id)
        .then(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Default Page"),
              type: "success",
              message: t("Activated page"),
            })
          );
          onReloadApp();
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Default Page"),
              type: "error",
              message: t("Failed to activate page"),
            })
          );
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div
            className="text-[15px] font-medium text-gray-900 leading-[18.15px]"
            style={{ letterSpacing: "0.075px" }}
          >
            {t("Instagram Profile")}
          </div>
        </div>
        <div className="col-span-12 lg:col-span-7">
          {appInfo.connected ? (
            loading ? (
              <ProfileSkeleton />
            ) : (
              <ProfileDisplay
                picture={connectedUser.profilePictureUrl}
                name={connectedUser.name}
                username={connectedUser.username}
                biography={connectedUser.biography}
                id={connectedUser.id}
              />
            )
          ) : (
            <div className="text-sm text-gray-500 py-2">
              {t("You need to connect your account first")}
            </div>
          )}
        </div>
        <div className="col-span-12 lg:col-span-2">{appInfo.connected && <ConnectedStatus />}</div>
      </div>
      <div className="border-b border-gray-200 h-[1px]"></div>
      <div className="grid grid-cols-12 gap-4 pt-4">
        <div className="col-span-12 lg:col-span-3">
          <div
            className="text-[15px] font-medium text-gray-900 leading-[18.15px] mb-2"
            style={{ letterSpacing: "0.075px" }}
          >
            {t("Instagram Pages")}
          </div>
          <div
            className="text-sm text-gray-500 leading-5"
            style={{ fontSize: "14px", letterSpacing: "0.07px" }}
          >
            {t("Select a default page to use in Social Content by AI")}
          </div>
        </div>
        {appInfo.connected ? (
          <div className="col-span-12 lg:col-span-9">
            {pagesLoading ? (
              <PageItemSkeleton />
            ) : connectedUserPages.length > 0 ? (
              connectedUserPages.map((page) => (
                <div key={page.id} className="grid grid-cols-12 gap-4 mb-4">
                  <div className="col-span-12 lg:col-span-8">
                    <PageItem
                      picture={page.picture}
                      name={page.name}
                      description={page.description}
                      id={page.instagramBusinessAccountId}
                      idLabel="page id"
                      isDefault={page.id === connectedUser.pageId}
                    />
                  </div>
                  <div className="col-span-12 lg:col-span-4">
                    <div className="flex items-center justify-end mr-4">
                      {page.id === connectedUser.pageId ? (
                        <button
                          type="button"
                          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-blue-600 text-white"
                          style={{ height: "28px" }}
                        >
                          <CheckCheck className="w-3 h-3" />
                          <span
                            className="text-xs font-medium leading-4"
                            style={{
                              fontSize: "12px",
                              letterSpacing: "0.06px",
                            }}
                          >
                            {t("Default Page")}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMakeDefault(page)}
                          type="button"
                          className="flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-gray-50 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                          <Stamp className="w-4 h-4" />
                          {t("Make Default")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 py-2">{t("No pages available")}</div>
            )}
          </div>
        ) : (
          <div className="col-span-12 lg:col-span-9"></div>
        )}
      </div>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={<OctagonAlertIcon />}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default InstagramSetting;
